import { GeckoComponentRect, RolodexEntryJson } from 'types'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export class GeckoPhotogrammetryTextureModel {
  /** Full file path within . */
  public file_path: string

  /** Direct gcloud URL generated by rolodex once requested */
  public texture_url: string | undefined

  constructor(
    public file_id: string,
    public name: string,
    public rect: GeckoComponentRect
  ) {
    this.file_path = 'images/' + file_id + '.png'
  }

  toJSON = (): Record<string, unknown> => ({
    file_id: this.file_id,
    name: this.name,
    rect: this.rect?.toJSON(),
  })

  static CreateFromJSON(
    id: string,
    json: unknown
  ): GeckoPhotogrammetryTextureModel {
    const record = json as Record<string, unknown>
    return new GeckoPhotogrammetryTextureModel(
      record.file_id as string,
      (record.name ?? '') as string,
      GeckoComponentRect.CreateFromJSON(
        id,
        record.rect! as Record<string, unknown>
      )!
    )
  }
}

export class GeckoPhotogrammetryNiraModel {
  constructor(
    public id: string,
    public name: string,
    public url: string
  ) {}

  toJSON = (): Record<string, unknown> => ({
    id: this.id,
    name: this.name,
    url: this.url,
  })

  static CreateFromJSON(json: unknown): GeckoPhotogrammetryNiraModel {
    const record = json as Record<string, unknown>
    return new GeckoPhotogrammetryNiraModel(
      record.id as string,
      record.name as string,
      record.url as string
    )
  }
}

export class GeckoPhotogrammetryKestrelModel {
  constructor(public entry_id: string) {}

  toJSON = (): Record<string, unknown> => ({
    entry_id: this.entry_id,
  })

  static CreateFromJSON(json: unknown): GeckoPhotogrammetryKestrelModel {
    const record = json as Record<string, unknown>
    return new GeckoPhotogrammetryKestrelModel(
      (record.entry_id ?? record.kestrel_model_id) as string
    )
  }
}

export interface TemperatureOverlaySettings {
  colormap: 'plasma' | 'infrared'
  temperature_range: [number, number]
}

/**
 * An entry for various photogrammetry representations of a component.
 *
 * A single model should represent a collection of textures / 3D models / links
 *  that were produced from the same set of images (one photogrammetry 'inspection').
 */
export class GeckoPhotogrammetryModel extends GeckoEntryBase<
  {
    name: string
    nira_links: Map<string, GeckoPhotogrammetryNiraModel>
    kestrel_links: Map<string, GeckoPhotogrammetryKestrelModel>
    textures: Map<string, GeckoPhotogrammetryTextureModel>
    primary_texture: string | undefined
    primary_nira_link: string | undefined
    primary_kestrel_link: string | undefined
    auto_load_nira: boolean
    temperature_overlay: TemperatureOverlaySettings | undefined
  },
  {
    organization: string
    site: string
    unit: string
    component: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>

    const niraLinksJSON = recordData.nira_links as Record<string, unknown>
    const nira_links = Object.keys(niraLinksJSON).reduce((resultObject, id) => {
      resultObject.set(
        id,
        GeckoPhotogrammetryNiraModel.CreateFromJSON(niraLinksJSON[id])
      )
      return resultObject
    }, new Map<string, GeckoPhotogrammetryNiraModel>())

    const kestrel_links = new Map<string, GeckoPhotogrammetryKestrelModel>()

    const temperature_overlay = recordData.temperature_overlay
      ? {
          colormap:
            ((recordData.temperature_overlay as Record<string, unknown>)
              .colormap as 'plasma' | 'infrared' | undefined) || 'plasma',
          temperature_range: (
            recordData.temperature_overlay as Record<string, unknown>
          ).temperature_range as [number, number],
        }
      : undefined

    const texturesJSON = recordData.textures as Record<string, unknown>
    const textures = Object.keys(texturesJSON).reduce((resultObject, id) => {
      resultObject.set(
        id,
        GeckoPhotogrammetryTextureModel.CreateFromJSON(id, texturesJSON[id])
      )
      return resultObject
    }, new Map<string, GeckoPhotogrammetryTextureModel>())

    super(
      json,
      {
        name: (recordData.name ?? '') as string,
        nira_links,
        kestrel_links,
        textures,
        primary_texture: recordData.primary_texture as string,
        primary_nira_link: recordData.primary_nira_link as string,
        primary_kestrel_link: recordData.primary_kestrel_link as string,
        auto_load_nira: (recordData.auto_load_nira as boolean) ?? false,
        temperature_overlay,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoPhotogrammetryModel(json)
  }
}
